import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../assets/styles/styles.scss";
import Metadata from "../../components/Metadata";
import Header from "../../components/dashboard/Header";
import Menu from "../../components/dashboard/Menu";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { Spin, Card, Image, Tag, Button, Rate } from "antd";
import { formatDateFr } from "../../config/helpers";

export default function UserDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderCount, setOrderCount] = useState(0);

  useEffect(() => {
    async function fetchUser() {
      setLoading(true);
      const userDoc = await getDoc(doc(db, "medicationUsers", id));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        let ordersQuery;
        if (userData.profileType === "patient") {
          ordersQuery = query(collection(db, "medicationOrders"), where("patientId", "==", id));
        } else if (userData.profileType === "driver") {
          ordersQuery = query(collection(db, "medicationOrders"), where("driverId", "==", id));
        } else if (userData.profileType === "pharmacy") {
          ordersQuery = query(collection(db, "medicationOrders"), where("pharmacyId", "==", id));
        }

        if (ordersQuery) {
          const ordersSnapshot = await getDocs(ordersQuery);
          setOrderCount(ordersSnapshot.size);
        }

        setUser(userData);
      }
      setLoading(false);
    }
    fetchUser();
  }, [id]);

  const handleChangeStatus = async (newStatus) => {
    await updateDoc(doc(db, "medicationUsers", id), { status: newStatus });
    setUser((prev) => ({ ...prev, status: newStatus }));
  };

  if (loading || !user) return <Spin tip="Chargement..." size="large" />;

  return (
    <>
      <h2 className="dashboard-title" style={{ marginBottom: 30 }}>
        Détails de l'utilisateur
      </h2>

      <Card
        title="Informations principales"
        style={{ marginBottom: 20 }}
        bordered={false}
        bodyStyle={{ padding: 20 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
          <Image
            width={80}
            height={80}
            src={user.files?.avatar || "/assets/images/avatar-placeholder.png"}
            style={{ borderRadius: "50%" }}
          />
          <div>
            {user.profileType === "pharmacy" ? (
              <h3 style={{ margin: 0 }}>{user.pharmacyName}</h3>
            ) : (
              <h3 style={{ margin: 0 }}>
                {user.firstName} {user.lastName}
              </h3>
            )}
            <p style={{ margin: "4px 0", fontSize: 14, color: "#666" }}>{user.email}</p>
            <Tag color={user.status === "approved" ? "green" : user.status === "banned" ? "red" : "orange"}>
              {user.status === "approved" ? "Approuvé" : user.status === "banned" ? "Banni" : user.status === "requested" ? "En attente de validation" : ""}
            </Tag>
          </div>
        </div>
        <p style={{ marginTop: 15 }}>
          <strong>Profil :</strong> {user.profileType}
        </p>
        <p>
          <strong>Téléphone :</strong> {user.phoneNumber}
        </p>
        <p>
          <strong>Date d'inscription :</strong>{" "}
          {user.createdAt?.toDate ? formatDateFr(user.createdAt.toDate()) : "—"}
        </p>
        <p>
          <strong>Nombre de commandes :</strong> {orderCount}
        </p>
      </Card>

      {user.profileType === "patient" && (
        <Card title="Informations patient" style={{ marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
          <p><strong>Numéro de sécurité :</strong> {user.securityNumber}</p>
          <p><strong>Date de naissance :</strong> {user.birthday?.seconds ? formatDateFr(new Date(user.birthday.seconds * 1000)) : "—"}</p>
          <div style={{ marginTop: 15 }}>
            <strong>Adresses :</strong>
            <ul>
              {user.addresses?.map((addr, idx) => (
                <li key={idx}>{addr.details}</li>
              ))}
            </ul>
          </div>
          <div style={{ marginTop: 15 }}>
            {user.files?.mutuelle && (
              <p><a href={user.files.mutuelle} target="_blank" rel="noreferrer">Voir la mutuelle</a></p>
            )}
            {user.files?.attestation && (
              <p><a href={user.files.attestation} target="_blank" rel="noreferrer">Voir l’attestation</a></p>
            )}
          </div>
        </Card>
      )}

      {user.profileType === "driver" && (
        <Card title="Informations livreur" style={{ marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
          <p><strong>Numéro de sécurité :</strong> {user.securityNumber}</p>
          <p><strong>Date de naissance :</strong> {user.birthday?.seconds ? formatDateFr(new Date(user.birthday.seconds * 1000)) : "—"}</p>
          <p><strong>Permis de conduire :</strong> 
            {user.files?.license && <a href={user.files.license} target="_blank" rel="noreferrer"> Voir</a>}
          </p>
          <p><strong>Note :</strong> <Rate disabled defaultValue={user.rating || 0} /> ({user.ratingsCount || 0})</p>
        </Card>
      )}

      {user.profileType === "pharmacy" && (
        <Card title="Informations pharmacie" style={{ marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
          <p><strong>Nom :</strong> {user.pharmacyName}</p>
          <p><strong>Adresse :</strong> {user.address?.details}</p>
          <p><strong>Localisation :</strong> {user.location}</p>
        </Card>
      )}

      <Card title="Actions" style={{ marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
        {user.status === "requested" && (
          <>
            <Button type="primary" style={{ marginRight: 10, background: "#6dbe42", border: "none" }} onClick={() => handleChangeStatus("approved")}>
              Approuver
            </Button>
            <Button danger onClick={() => handleChangeStatus("banned")}>
              Bannir
            </Button>
          </>
        )}
        {user.status === "approved" && (
          <Button danger onClick={() => handleChangeStatus("banned")}>
            Bannir
          </Button>
        )}
        {user.status === "banned" && (
          <Button type="primary" style={{ background: "#6dbe42", border: "none" }} onClick={() => handleChangeStatus("approved")}>
            Réactiver
          </Button>
        )}
      </Card>

      <div style={{ display: "flex", gap: 10 }}>
        <Button
          type="default"
          style={{ color: "#FF2222", borderColor: "#FF2222" }}
          onClick={() => navigate("/admin/public-app-users")}
        >
          Retour à la liste
        </Button>
      </div>
    </>
  );
}
